import { OPERATION_ROLES, type OperationId } from '@app/api/roles';
import type { Role } from './sessionContext';
import { useCurrentUser } from './useCurrentUser';

/**
 * Matriz papel × ação de FR-009 — as doze linhas da tabela do spec, uma a uma.
 *
 * Quem consulta é **toda** ação de escrita da interface, para **omitir** o botão
 * (não desabilitar) quando o papel não permite — FR-010. O servidor verifica de
 * novo em cada chamada: esta matriz é conveniência de tela, nunca a segurança.
 */
export type Action =
  /** 1. Ver todas as telas (exceto Usuários) e valores não secretos de configuração. */
  | 'ver'
  /** 2. Simular · reproduzir decisão · usar o mapa · ler auditoria. */
  | 'analisar'
  /** 3. Discovery agora. */
  | 'discovery.executar'
  /** 4. Atribuir/editar identidade · papel de identidade. */
  | 'identidade.editar'
  /** 5. Aprovar / rejeitar · Tentar de novo. */
  | 'aprovacao.decidir'
  /** 6. Access Control (salvar contrato candidato). */
  | 'access_control.salvar'
  /** 7. Criar/editar/excluir/publicar políticas. */
  | 'politica.editar'
  /** 8. Reconciliar admission · republicar PDP. */
  | 'runtime.reconciliar'
  /** 9. Importar do gateway · criar/editar/excluir resources · classificar risco. */
  | 'resource.editar'
  /** 10. Infraestrutura (salvar grupos, testar conexão com gravação) · Aplicar imagem. */
  | 'infra.salvar'
  /** 11. Configurações do tenant · Remover identidade. */
  | 'tenant.editar'
  /** 12. Usuários. */
  | 'usuario.editar';

/**
 * A operação do contrato que **representa** cada linha da matriz.
 *
 * Isto é um ponteiro, não política: os papéis de cada linha saem de
 * `OPERATION_ROLES`, gerado do `x-roles` do contrato congelado (decisão P12) —
 * a mesma fonte que o backend aplica por rota. Ninguém digita "approver pode
 * aprovar" duas vezes, em dois lugares que podem divergir.
 *
 * `useCan(acao)` existe ao lado de `podeOperar(operacao)` porque uma seção de
 * tela costuma ter várias operações da mesma linha ("criar/editar/excluir/publicar
 * políticas"); quando o botão tem uma operação só, `podeOperar` é mais direto.
 */
const ACTION_OPERATION: Record<Action, OperationId> = {
  ver: 'listIdentities',
  analisar: 'simulateCall',
  'discovery.executar': 'runDiscovery',
  'identidade.editar': 'assignIdentity',
  'aprovacao.decidir': 'approveApproval',
  'access_control.salvar': 'submitAccessControlCandidate',
  'politica.editar': 'createPolicy',
  'runtime.reconciliar': 'reconcileAdmission',
  'resource.editar': 'createResource',
  'infra.salvar': 'updateInfraConfigGroup',
  'tenant.editar': 'updateTenant',
  'usuario.editar': 'createUser',
};

/** Todas as ações da matriz — a lista que os testes percorrem. */
export const ACTIONS = Object.keys(ACTION_OPERATION) as Action[];

/**
 * A matriz do FR-009 **derivada do contrato**. Exportada para leitura e teste;
 * mudar papel de operação se faz no `api-openapi.yaml` + `pnpm gen:api`.
 */
export const MATRIZ_FR009: Record<Action, readonly Role[]> = Object.fromEntries(
  ACTIONS.map((acao) => [acao, OPERATION_ROLES[ACTION_OPERATION[acao]]]),
) as Record<Action, readonly Role[]>;

/**
 * Versão pura de `useCan`. Papel desconhecido (sessão em modo degradado) responde
 * `true`: a interface não esconde por não saber quem é — o servidor recusa.
 */
export function canFazer(papel: Role | null, acao: Action): boolean {
  return canOperar(papel, ACTION_OPERATION[acao]);
}

/**
 * `useCan('resource.editar') && <Button>Novo resource</Button>` — a forma de usar.
 * Nunca `disabled={!useCan(...)}`: FR-010 manda **omitir**.
 */
export function useCan(acao: Action): boolean {
  const { papel } = useCurrentUser();
  return canFazer(papel, acao);
}

/**
 * Gate por **operação do contrato** — o caminho preferido para botão de escrita:
 *
 * ```tsx
 * {usePodeOperar('createResource') && <LoadingButton …>Novo resource</LoadingButton>}
 * ```
 *
 * A vantagem sobre `useCan(acao)` é a prova: `OPERATION_ROLES` é gerado do
 * `x-roles` do contrato congelado, o mesmo que o backend aplica por rota — as duas
 * pontas saem da mesma fonte, e mudança de papel propaga por `pnpm gen:api`.
 * `useCan` continua para o que é ação de tela sem uma operação única por trás.
 */
export function canOperar(papel: Role | null, operation: OperationId): boolean {
  const roleList = OPERATION_ROLES[operation];
  // Lista vazia = operação sem sessão (health, login, fluxo do Google).
  if (roleList.length === 0) return true;
  // Papel desconhecido: mesma regra do resto do console — o servidor recusa.
  if (!papel) return true;
  return roleList.includes(papel);
}

export function useCanOperar(operation: OperationId): boolean {
  const { papel } = useCurrentUser();
  return canOperar(papel, operation);
}
